import { Game } from '../../features/game/types/Game'
import { internalApiserverApi } from './internal-apiserver'

export type InternalGameSearchParams = {
  term: string
  marketIso: string
  conventionalSubgenreId?: string
}

export const internalGameSearchFields = [
  'name',
  'appId',
  'artist',
  'genre',
  'genreId',
  'subGenre',
  'subGenreId',
  'icon',
  'icons',
  'gpsPerMarket',
  'markets',
  'conventionalSubgenre',
]

const internalGameListFields = ['appId', 'id', 'name', 'artist', 'icon', 'icons', 'internal', 'owner', 'markets']

const internalGamesApi = internalApiserverApi.injectEndpoints({
  endpoints: (builder) => ({
    // Legacy `gameService.search`: at most 20 games, all owners, analysed or not.
    searchInternalGames: builder.query<Game[], InternalGameSearchParams>({
      query: ({ term, marketIso, conventionalSubgenreId }) => ({
        url: 'i/games/search',
        method: 'POST',
        body: {
          term,
          marketIso,
          include: internalGameSearchFields.join(','),
          ...(conventionalSubgenreId ? { conventionalSubgenreIds: conventionalSubgenreId } : {}),
        },
      }),
      transformResponse: (games: Game[]) => games.map((game) => new Game(game)),
    }),
    // Any game, analysed or not, as legacy's `gameService.get(id, true)`.
    getInternalGame: builder.query<Game, string>({
      query: (id) => ({ url: `i/games/${id}`, params: { gameOnly: true, marketIso: 'us' } }),
      transformResponse: (response: { game: Game }) => new Game(response.game),
    }),
    // `gameIds` takes app ids here, as legacy's subgenre example games.
    getInternalGamesByAppIds: builder.query<Game[], number[]>({
      query: (appIds) => ({ url: 'games/list', method: 'POST', body: { gameIds: appIds, include: internalGameListFields.join(',') } }),
      transformResponse: (games: Game[]) => games.map((game) => new Game(game)),
    }),
    // Legacy `gameService.getMultiple(ids, fields, true)`. Ids with no game are left out.
    // One id answers `{ game }`, fails without `marketIso`, and answers some other game when the id is unknown.
    getInternalGamesByIds: builder.query<Game[], string[]>({
      query: (ids) => ({ url: `games/${ids.join(',')}`, params: { include: internalGameListFields.join(','), gameOnly: true, marketIso: 'us' } }),
      transformResponse: (response: Game[] | { game?: Game } | null, _, ids) => {
        const games = Array.isArray(response) ? response : response?.game ? [response.game] : []
        return games.filter((game) => ids.includes(game.id)).map((game) => new Game(game))
      },
    }),
  }),
})

export const { useSearchInternalGamesQuery, useGetInternalGameQuery, useGetInternalGamesByAppIdsQuery, useGetInternalGamesByIdsQuery } = internalGamesApi
